/**
 * « Placer à table » — attribue un numéro de table à une réservation.
 *
 * Le champ est libre (TEXT côté base) parce que les salles ne numérotent pas
 * toutes en entiers : « 12b », « Terrasse 3 ». Les tables ouvertes remontées par
 * Cashpad (`/pos/dashboard-summary`) sont proposées en raccourci, mais ne sont
 * qu'une aide à la saisie : au moment où le staff place une résa du soir, la
 * table visée n'est en général pas encore ouverte côté caisse. Le chargement est
 * best-effort — une caisse indisponible n'empêche pas de placer à la main.
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import './PlaceAtTableSheet.css';

interface Props {
  reservation: { id: number; nom: string; couverts: number; table_num: string | null };
  onClose: () => void;
  /** Appelé après un PATCH réussi (rafraîchissement de la liste appelante). */
  onSaved: () => void;
}

export function PlaceAtTableSheet({ reservation, onClose, onSaved }: Props) {
  const [value, setValue] = useState(reservation.table_num ?? '');
  const [openTables, setOpenTables] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Masque la TabBar tant que la feuille est ouverte (même convention que les
  // autres sheets staff).
  useEffect(() => {
    document.body.classList.add('modal-sheet-open');
    return () => document.body.classList.remove('modal-sheet-open');
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/pos/dashboard-summary')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d?.tables) return;
        setOpenTables(
          (d.tables as { number: number }[])
            .map((t) => t.number)
            .filter((n) => typeof n === 'number')
            .sort((a, b) => a - b),
        );
      })
      .catch(() => { /* caisse indisponible → saisie manuelle uniquement */ });
    return () => { cancelled = true; };
  }, []);

  const save = async (next: string | null) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const r = await apiFetch(`/reservations/${reservation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ table_num: next }),
      });
      if (!r.ok) {
        setError(r.status === 403 ? 'Action réservée à l’admin' : 'Enregistrement échoué');
        setSaving(false);
        return;
      }
      onSaved();
      onClose();
    } catch {
      setError('Erreur réseau — réessaie');
      setSaving(false);
    }
  };

  const trimmed = value.trim();

  return (
    <div className="pat-backdrop" onClick={onClose}>
      <div className="pat-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="pat-header">
          <h2>Placer à table</h2>
          <p>{reservation.nom} · {reservation.couverts} couvert{reservation.couverts > 1 ? 's' : ''}</p>
        </div>

        <label className="pat-field">
          <span>Numéro de table</span>
          <input
            className="pat-input"
            value={value}
            onChange={(e) => setValue(e.target.value.slice(0, 20))}
            placeholder="12, 12b, Terrasse 3…"
            autoFocus
          />
        </label>

        {openTables.length > 0 && (
          <div className="pat-suggest">
            <span className="pat-suggest-label">Tables ouvertes</span>
            <div className="pat-chips">
              {openTables.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`pat-chip${trimmed === String(n) ? ' active' : ''}`}
                  onClick={() => setValue(String(n))}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <p className="pat-error">{error}</p>}

        <div className="pat-actions">
          {reservation.table_num && (
            <button type="button" className="pat-btn pat-btn-ghost" onClick={() => save(null)} disabled={saving}>
              Retirer la table
            </button>
          )}
          <button type="button" className="pat-btn pat-btn-secondary" onClick={onClose} disabled={saving}>
            Annuler
          </button>
          <button
            type="button"
            className="pat-btn pat-btn-primary"
            onClick={() => save(trimmed || null)}
            disabled={saving || !trimmed}
          >
            {saving ? 'Enregistrement…' : 'Placer'}
          </button>
        </div>
      </div>
    </div>
  );
}
